import { redirect } from '@tanstack/react-router'
import { useEffect } from 'react'

// Per-list-page filter persistence.
//
// Filters normally live only in the URL search params, so a hard refresh keeps them but navigating
// away (e.g. via the sidebar, which links to the bare path) and coming back drops them. These helpers
// mirror the tracked search params into localStorage so a list page can restore the user's last
// filters on entry, and let a page apply a default filter set on the very first visit.
//
// The snapshot stored under a page's key doubles as a "touched" marker: once it exists (even empty,
// meaning the user explicitly cleared everything to see all rows) defaults are no longer applied.

const STORAGE_PREFIX = 'ft-list-filters:'

export function readPersistedFilters(storageKey: string): Record<string, unknown> | null {
	try {
		const raw = localStorage.getItem(STORAGE_PREFIX + storageKey)
		if (raw === null) return null
		const parsed = JSON.parse(raw)
		if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
			return parsed as Record<string, unknown>
		}
		return null
	} catch {
		// Corrupt JSON, privacy mode, or storage unavailable: fall back to "no snapshot".
		return null
	}
}

export function writePersistedFilters(storageKey: string, values: Record<string, unknown>): void {
	try {
		localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(values))
	} catch {
		// Best-effort: ignore quota/privacy-mode errors.
	}
}

// Keep only tracked keys that carry a meaningful value. Empty arrays and `undefined` are treated as
// "not set" so an empty selection never masks a default and never bloats the stored snapshot.
function pickMeaningfulFilters(source: Record<string, unknown>, keys: readonly string[]): Record<string, unknown> {
	const result: Record<string, unknown> = {}
	for (const key of keys) {
		const value = source[key]
		if (value === undefined) continue
		if (Array.isArray(value) && value.length === 0) continue
		result[key] = value
	}
	return result
}

/**
 * Call from a route `beforeLoad`. When the URL carries none of the tracked filter keys (a fresh
 * entry such as a sidebar click), restore the last persisted snapshot, or the provided defaults on
 * the first ever visit, by throwing a redirect to the same path with the restored search applied.
 *
 * Only runs on `cause === 'enter'` so hover-preloads and in-place search updates (`stay`) do not
 * rewrite the URL. When the URL already carries a tracked filter (refresh, bookmark, shared link)
 * it is respected and nothing happens.
 */
export function restoreListFilters<TSearch extends Record<string, unknown>>(options: {
	/** Stable key identifying this list page, e.g. `'promotions'`. */
	storageKey: string
	/** URL path of the list page, e.g. `'/promotions'`. Restore only runs on exactly this path. */
	to: string
	/** Current URL pathname (from `beforeLoad`'s `location`). */
	pathname: string
	/** `beforeLoad` cause — restore only on a real route entry, not preload or stay. */
	cause: 'preload' | 'enter' | 'stay'
	/** Search params this page persists/restores (exclude pagination like `limit`/`offset`). */
	keys: readonly (keyof TSearch & string)[]
	/** The current (validated) search params. */
	search: TSearch
	/** Applied only on the first ever visit (no snapshot yet). */
	defaults?: Partial<TSearch>
}): void {
	const { storageKey, to, pathname, cause, keys, search, defaults } = options

	// Preload (sidebar hover) and stay (search-param change while already matched) must not restore.
	if (cause !== 'enter') return

	// A layout `route.tsx` runs `beforeLoad` for its child routes too (e.g. `/promotions/create`).
	// Only restore when we're on the bare list path, otherwise we'd bounce the user off a child page.
	if (pathname !== to) return

	// If the URL already carries any tracked filter, respect it (refresh / bookmark / shared link).
	const urlHasFilter = keys.some(key => search[key] !== undefined)
	if (urlHasFilter) return

	const snapshot = readPersistedFilters(storageKey)
	// A present-but-empty snapshot means "user cleared everything" -> show all, no defaults.
	const source = snapshot ?? (defaults as Record<string, unknown> | undefined) ?? {}
	const next = pickMeaningfulFilters(source, keys)

	if (Object.keys(next).length === 0) return

	throw redirect({
		// `to` is a real route path from the call site; the typed-router `to` union can't see that,
		// so cast past it. Validity is guaranteed by the caller.
		to: to as '/',
		search: (prev: Record<string, unknown>) => ({ ...prev, ...next }),
		replace: true
	})
}

/**
 * Persist the tracked search params to localStorage whenever they change. Writing an empty snapshot
 * is intentional: it records that the user has interacted so defaults are not re-applied later.
 */
export function usePersistListFilters<TSearch extends Record<string, unknown>>(
	storageKey: string,
	keys: readonly (keyof TSearch & string)[],
	search: TSearch
): void {
	const snapshot = pickMeaningfulFilters(search, keys)
	// Serialize so the effect only re-runs when the tracked values actually change, not on every
	// render that produces a new search object identity.
	const serialized = JSON.stringify(snapshot)
	useEffect(() => {
		writePersistedFilters(storageKey, JSON.parse(serialized) as Record<string, unknown>)
	}, [storageKey, serialized])
}
